/**
 * Ops Command Center surface helpers — Map HUD + plane legend (Soft≠Live).
 * Soft counts only when fleet Soft theatre is armed (127).
 *
 * v355 — Field Glass volume plate: radar rings · cover meter · enter spring.
 */
import { html, go, M, SPRING } from "./ui.js";
import { isLocalHost } from "./pathseal.js";
import { softCommandWorkbenchOf } from "./soft-command-loop.js";

/**
 * Top map HUD — spatial side of Map ∥ Run workbench split.
 */
export function OpsCommandHud({ fleet, seat, mappedN, totalN, toast }) {
  const local = isLocalHost();
  const softOn = !!(local && fleet && fleet.enabled && fleet.state);
  const wb = softCommandWorkbenchOf(softOn ? fleet.state : null, {
    local,
    livePlayerWired: false,
  });
  const sup = wb.supervisor || {};
  const human = sup.mode === "human" || sup.paused;
  const k = wb.kpis || {};
  const jobs = softOn ? (wb.dispatches || []).length : 0;
  const incs = softOn ? (wb.incidents || []).length : 0;
  const mission = softOn ? k.mission || 0 : 0;
  const plantSoft = softOn ? k.plant || 0 : 0;

  return html`<div
    className="ops-cc-hud"
    data-cmc-residual="ops-command-hud"
    data-soft=${softOn ? "1" : "0"}
    data-supervisor=${human ? "human" : softOn ? "ai" : "live"}
    aria-label="Command Center HUD">
    <div className="ops-cc-hud__brand">
      <span className="ops-cc-hud__mark">CROC</span>
      <div>
        <p className="ops-cc-hud__title">Command Center</p>
        <p className="ops-cc-hud__sub">
          Map = space · Run = operate
          ${softOn ? " · lab" : " · site layers"}
        </p>
      </div>
    </div>

    <div className="ops-cc-hud__mode" data-tone=${human ? "human" : softOn ? "ai" : "live"}>
      ${human ? "HUMAN" : softOn ? "AI SOFT" : "LIVE"}
    </div>

    <div className="ops-cc-hud__stats" role="group" aria-label="Spatial counts">
      <span title="Device pins on the map">
        <i>BOARDS</i><b>${mappedN}/${totalN || 0}</b>
      </span>
      <span title="Soft mission marks · 127 only" data-soft=${softOn ? "1" : "0"}>
        <i>MISSION</i><b>${softOn ? mission : "—"}</b>
      </span>
      <span title="Active Soft jobs">
        <i>JOBS</i><b>${softOn ? jobs : "—"}</b>
      </span>
      <span title="Soft incidents">
        <i>CASES</i><b>${softOn ? incs : "—"}</b>
      </span>
      <span title="Soft plant inventory count" data-soft=${softOn ? "1" : "0"}>
        <i>BOARDS</i><b>${softOn ? plantSoft : "—"}</b>
      </span>
    </div>

    <div className="ops-cc-hud__gap" title="Browser multi-camera path">
      <i>LIVE</i>
      <b data-gap="1">not wired</b>
    </div>

    <div className="ops-cc-hud__acts">
      <button
        type="button"
        className="btn btn--xs"
        onClick=${() => go("/ops/workbench")}
        title="Operational Workbench">
        Run
      </button>
      ${softOn && fleet.humanStopAll
        ? html`<button
            type="button"
            className="btn btn--xs"
            data-tone="warn"
            onClick=${() => {
              fleet.humanStopAll("HUD STOP ALL · Soft");
              toast && toast("STOP ALL Soft · AI paused", "warn");
            }}>
            STOP
          </button>`
        : null}
      <span className="ops-cc-hud__role">${(seat && seat.role) || "—"}</span>
    </div>
  </div>`;
}

/** Bottom-left legend — plant · mission · volume · cover. Honesty: Plant · Volume */
export function OpsMapPlaneLegend({ softOn }) {
  return html`<div
    className="ops-cc-legend"
    data-soft=${softOn ? "1" : "0"}
    aria-label="Map layer legend">
    <span className="ops-cc-legend__item" data-plane="plant">
      <i></i> Boards
    </span>
    <span className="ops-cc-legend__item" data-plane="mission" data-soft=${softOn ? "1" : "0"}>
      <i></i> Jobs
    </span>
    <span className="ops-cc-legend__item" data-plane="volume" data-soft=${softOn ? "1" : "0"}>
      <i></i> Areas
    </span>
    <span className="ops-cc-legend__item" data-plane="route">
      <i></i> Path
    </span>
    ${softOn
      ? html`<span className="ops-cc-legend__item" data-plane="thumb">
          <i></i> Cover
        </span>`
      : null}
  </div>`;
}

/**
 * Floating volume focus plate — map side of Now/Volumes/Run loop.
 * Signature visual: Field Glass radar · cover fill motion · enter spring.
 * Soft≠Live · Live plant counts real; Soft pads 127 only.
 */
export function OpsVolumeMapPlate({ ctx, onRun, onVolumes, onClear }) {
  if (!ctx || !ctx.ok) return null;
  const coverPct = Math.min(100, Math.max(8, ((ctx.coverN || 0) / 3) * 100));
  const ready = ctx.ready || "open";
  return html`<${M.div}
    key=${"vol-plate-" + (ctx.id || ctx.label || "x")}
    className="ops-vol-plate ops-vol-plate--field"
    data-ready=${ready}
    data-tone=${ctx.tone || ""}
    data-soft=${ctx.soft ? "1" : "0"}
    data-cmc-residual="ops-volume-map-plate"
    data-field="v366"
    initial=${{ opacity: 0, y: 16, scale: 0.97 }}
    animate=${{ opacity: 1, y: 0, scale: 1 }}
    exit=${{ opacity: 0, y: 10, scale: 0.98 }}
    transition=${SPRING}
    aria-label=${"Volume focus · " + (ctx.label || "")}>
    <div className="ops-vol-plate__radar" aria-hidden="true">
      <i className="ops-vol-plate__ring"></i>
      <i className="ops-vol-plate__ring ops-vol-plate__ring--2"></i>
      <s className="ops-vol-plate__sweep"></s>
      <b className="ops-vol-plate__dot" data-ready=${ready}></b>
    </div>
    <div className="ops-vol-plate__body">
      <div className="ops-vol-plate__head">
        <div className="ops-vol-plate__head-text">
          <em>${ctx.roleLabel || "Volume"}</em>
          <b>${ctx.label || "—"}</b>
        </div>
        <span className="ops-vol-plate__ready" data-ready=${ready}>
          ${ctx.readyLabel || "Open"}
        </span>
      </div>
      <div className="ops-vol-plate__meter" aria-hidden="true">
        <i style=${{ width: coverPct + "%" }} data-ready=${ready}></i>
      </div>
      <p className="ops-vol-plate__meta">
        <span><u>${ctx.coverN || 0}</u> cover</span>
        <span><u>${ctx.plantN || 0}</u> boards</span>
        ${ctx.unitN
          ? html`<span><u>${ctx.unitN}</u> unit</span>`
          : null}
      </p>
      <div className="ops-vol-plate__acts">
        <button type="button" className="ops-vol-plate__act ops-vol-plate__act--go" onClick=${() => onRun && onRun()}>
          Run here
        </button>
        <button type="button" className="ops-vol-plate__act" onClick=${() => onVolumes && onVolumes()}>
          Areas
        </button>
        <button type="button" className="ops-vol-plate__act ops-vol-plate__act--muted" onClick=${() => onClear && onClear()}>
          Clear
        </button>
      </div>
    </div>
  <//>`;
}

/**
 * Map ambient volume frame — concentric glass rings (pointer-events none).
 * Shows only while a volume is focused · soft motion feedback of space lock.
 */
export function OpsVolumeMapRim({ ctx, flashKey }) {
  if (!ctx || !ctx.ok) return null;
  return html`<div
    className="ops-vol-rim"
    data-ready=${ctx.ready || "open"}
    data-tone=${ctx.tone || ""}
    data-flash=${flashKey ? "1" : "0"}
    key=${"rim-" + (flashKey || "steady") + "-" + (ctx.id || "")}
    aria-hidden="true">
    <i className="ops-vol-rim__halo"></i>
    <i className="ops-vol-rim__orbit"></i>
    <i className="ops-vol-rim__orbit ops-vol-rim__orbit--2"></i>
    <s className="ops-vol-rim__burst"></s>
  </div>`;
}
